import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from './common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import StatCards from './components/StatCards';
import PriceChart from './components/PriceChart';
import PriceTable from './components/PriceTable';
import NewsTable from './components/NewsTable';
import PredictionTable from './components/PredictionTable';
import SentimentAnalysis from './components/SentimentAnalysis';
import { 
  generateMockPriceData, 
  generateMockNewsData, 
  generatePredictions,
  getCurrentPrice,
  analyzeSentiment
} from './services/mockData';

const { FiActivity, FiTrendingUp, FiRefreshCw, FiBarChart3, FiNewspaper, FiTarget } = FiIcons;

function App() {
  const [currentPrice, setCurrentPrice] = useState(0.52);
  const [priceData, setPriceData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [predictions, setPredictions] = useState([]);
  const [sentimentAnalysis, setSentimentAnalysis] = useState({});
  const [activeTab, setActiveTab] = useState('overview');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const initializeData = () => {
      setIsLoading(true);
      
      const mockPriceData = generateMockPriceData();
      const mockNewsData = generateMockNewsData();
      const mockPredictions = generatePredictions(mockPriceData, mockNewsData);
      const sentiment = analyzeSentiment(mockNewsData);
      
      setPriceData(mockPriceData);
      setNewsData(mockNewsData);
      setPredictions(mockPredictions);
      setSentimentAnalysis(sentiment);
      setCurrentPrice(getCurrentPrice());
      setLastUpdated(new Date());
      setIsLoading(false);
    };

    initializeData();

    // Update current price every 30 seconds
    const priceInterval = setInterval(() => {
      setCurrentPrice(getCurrentPrice());
      setLastUpdated(new Date());
    }, 30000);

    return () => clearInterval(priceInterval);
  }, []);

  const refreshData = () => {
    setIsLoading(true);
    setTimeout(() => {
      const mockPriceData = generateMockPriceData();
      const mockNewsData = generateMockNewsData();
      const mockPredictions = generatePredictions(mockPriceData, mockNewsData);
      const sentiment = analyzeSentiment(mockNewsData);
      
      setPriceData(mockPriceData);
      setNewsData(mockNewsData);
      setPredictions(mockPredictions);
      setSentimentAnalysis(sentiment);
      setCurrentPrice(getCurrentPrice());
      setLastUpdated(new Date());
      setIsLoading(false);
    }, 1000);
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiActivity },
    { id: 'analysis', label: 'Analysis', icon: FiBarChart3 },
    { id: 'news', label: 'News', icon: FiNewspaper },
    { id: 'predictions', label: 'Predictions', icon: FiTarget }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-xrp-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading XRP data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="flex items-center">
                  <div className="w-8 h-8 bg-xrp-600 rounded-full flex items-center justify-center mr-3">
                    <span className="text-white font-bold text-sm">XRP</span>
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">XRP News Tracker</h1>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Last updated: {lastUpdated.toLocaleTimeString()}
              </div>
              <button
                onClick={refreshData}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-xrp-500 text-xrp-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <SafeIcon icon={tab.icon} className="w-4 h-4 mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <StatCards 
              currentPrice={currentPrice}
              priceData={priceData}
              newsData={newsData}
              sentimentAnalysis={sentimentAnalysis}
            />
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="lg:col-span-2">
                <PriceChart data={priceData} newsData={newsData} />
              </div>
              <PriceTable data={priceData} newsData={newsData} />
              <NewsTable newsData={newsData} />
            </div>
          </motion.div>
        )}

        {activeTab === 'analysis' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SentimentAnalysis 
              newsData={newsData}
              sentimentAnalysis={sentimentAnalysis}
            />
          </motion.div>
        )}

        {activeTab === 'news' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <NewsTable newsData={newsData} />
          </motion.div>
        )}

        {activeTab === 'predictions' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <PredictionTable predictions={predictions} currentPrice={currentPrice} />
          </motion.div>
        )}
      </main>
    </div>
  );
}

export default App;